/**
 * The CEO page's arithmetic, ported from the published report.
 *
 * Direct ports of `atomsIn`, `dayValue`, `deriveGeo`, `poolAtoms`, `brandPool`,
 * `marketPool` and `ownDailySeries` in the live CEO report. Copied rather than
 * reimplemented: the in-app version read a pre-aggregated rollup and therefore
 * disagreed with the report on every figure, and disagreed on market share by
 * an order of magnitude because the rollup measured share against a different
 * category universe.
 *
 * EVERY RATIO IS A RATIO OF SUMS. Pool the atoms over the range, then divide
 * once. Averaging daily percentages is the recurring way to get an answer that
 * looks plausible and is wrong — it weights a quiet day the same as a busy one,
 * and it silently absorbs a coverage step-change instead of showing it.
 */

export type Basis = "mrp" | "sp";

export interface Atom {
  d: string;
  n: number;
  dn: number;
  pr: number;
  au: number;
  eu: number;
  spe: number;
  mrpe: number;
  /** Measured rupees from the brand's own portal. Rivals never have this. */
  va?: number;
}

export interface Pooled {
  name: string;
  days: number;
  dates: string[];
  atoms: Required<Omit<Atom, "d">>;
  /** True when portal actuals cover this entity — it is measured, not estimated. */
  measured: boolean;
  osa: number | null;
  listing: number | null;
  asp: number | null;
  mrpPerUnit: number | null;
  units: number;
  unitsCrawl: number;
  sp: number | null;
  mrp: number | null;
  gmv: number | null;
  drr: number | null;
  disc: number | null;
  /** Daily OSA %, for the in-row sparkline. */
  series: Array<number | null>;
  /** Daily value on the selected basis. */
  gmvSeries: Array<number | null>;
}

export function atomsIn(
  rows: Atom[] | undefined,
  f: string,
  t: string,
): Atom[] {
  return (rows ?? []).filter((r) => r.d >= f && r.d <= t);
}

/**
 * One day's value on the selected basis.
 *
 * PORTAL VALUE WINS on the MRP basis: `va` is the brand's own download, it
 * matches the partner portal to the rupee, and it already contains every
 * listing — including the second product_id Blinkit issues for several SKUs,
 * which the crawl sees as a separate product. Only without it do we fall back
 * to portal units at a crawl-implied unit price, and then to the pure crawl
 * estimate for rivals, who have no portal at all.
 */
export function dayValue(r: Atom, basis: Basis): number | null {
  const va = r.va ?? 0;
  if (basis !== "sp" && va > 0) return va;
  if (!r.eu) return r.au ? null : 0;
  if (r.au) return r.au * (basis === "sp" ? r.spe / r.eu : r.mrpe / r.eu);
  return basis === "sp" ? r.spe : r.mrpe;
}

export function deriveGeo(
  name: string,
  rows: Atom[],
  dates: string[],
  basis: Basis,
): Pooled {
  const a = { n: 0, dn: 0, pr: 0, au: 0, va: 0, eu: 0, spe: 0, mrpe: 0 };
  for (const r of rows) {
    a.n += r.n || 0;
    a.dn += r.dn || 0;
    a.pr += r.pr || 0;
    a.au += r.au || 0;
    a.va += r.va || 0;
    a.eu += r.eu || 0;
    a.spe += r.spe || 0;
    a.mrpe += r.mrpe || 0;
  }
  const measured = a.au > 0;
  const asp = a.eu ? a.spe / a.eu : null;
  const mrpU = a.eu ? a.mrpe / a.eu : null;
  const sp = measured ? (asp !== null ? a.au * asp : null) : a.spe;
  const mrp =
    a.va > 0 ? a.va : measured ? (mrpU !== null ? a.au * mrpU : null) : a.mrpe;
  return {
    name,
    days: rows.length,
    dates,
    atoms: a,
    measured,
    osa: a.dn ? (a.n / a.dn) * 100 : null,
    listing: a.dn && a.pr ? (a.pr / a.dn) * 100 : null,
    asp,
    mrpPerUnit: a.va > 0 && a.au > 0 ? a.va / a.au : mrpU,
    units: measured ? a.au : a.eu,
    unitsCrawl: a.eu,
    sp,
    mrp,
    gmv: basis === "sp" ? sp : mrp,
    drr: rows.length ? (measured ? a.au : a.eu) / rows.length : null,
    disc: a.mrpe ? (1 - a.spe / a.mrpe) * 100 : null,
    series: rows.map((x) => (x.dn ? (x.n / x.dn) * 100 : null)),
    gmvSeries: rows.map((x) => dayValue(x, basis)),
  };
}

/** Every key of a {name: atoms[]} map, pooled over the range. Empty keys drop. */
export function poolAtoms(
  map: Record<string, Atom[]>,
  f: string,
  t: string,
  basis: Basis,
): Pooled[] {
  return Object.entries(map)
    .map(([name, rows]) => {
      const r = atomsIn(rows, f, t);
      if (!r.length) return null;
      return deriveGeo(
        name,
        r,
        r.map((x) => x.d),
        basis,
      );
    })
    .filter((x): x is Pooled => x !== null);
}

/**
 * O'cean pan-India: every city, via the residual bucket. Rupee totals are
 * SUMMED FROM THE GEOGRAPHIES, exactly as the Operations page does, so the two
 * pages' headline GMV can never drift apart.
 */
export function national(
  geoAtoms: Record<string, Record<string, Atom[]>>,
  own: string,
  f: string,
  t: string,
  basis: Basis,
): Pooled | null {
  const byDay = new Map<string, Atom>();
  for (const rows of Object.values(geoAtoms[own] ?? {})) {
    for (const r of atomsIn(rows, f, t)) {
      const a = byDay.get(r.d) ?? {
        d: r.d,
        n: 0,
        dn: 0,
        pr: 0,
        au: 0,
        eu: 0,
        spe: 0,
        mrpe: 0,
        va: 0,
      };
      a.n += r.n || 0;
      a.dn += r.dn || 0;
      a.pr += r.pr || 0;
      a.au += r.au || 0;
      a.eu += r.eu || 0;
      a.spe += r.spe || 0;
      a.mrpe += r.mrpe || 0;
      a.va = (a.va ?? 0) + (r.va ?? 0);
      byDay.set(r.d, a);
    }
  }
  const dates = [...byDay.keys()].sort();
  if (!dates.length) return null;
  const rows = dates
    .map((d) => byDay.get(d))
    .filter((x): x is Atom => x !== undefined);
  const out = deriveGeo(own, rows, dates, basis);

  /**
   * RUPEE TOTALS ARE SUMMED FROM THE GEOGRAPHIES, not re-derived from the
   * pooled atoms.
   *
   * `deriveGeo` computes value as units x a unit price implied by the POOLED
   * atoms, and that is not the same number as the sum of each geography's own
   * value — the geographies have different price mixes, so a national unit
   * price is a weighted average that no geography actually charges. Without
   * this overwrite the By geography rows do not add up to the headline above
   * them, and the CEO page disagrees with Operations, which does sum from the
   * geographies.
   *
   * On MRP this is an exact identity anyway (both sides are the portal's own
   * SUM(va)); it is the SP basis where the two diverge.
   */
  let sp = 0;
  let mrp = 0;
  for (const geoRows of Object.values(geoAtoms[own] ?? {})) {
    const r = atomsIn(geoRows, f, t);
    if (!r.length) continue;
    const g = deriveGeo(
      own,
      r,
      r.map((x) => x.d),
      basis,
    );
    sp += g.sp ?? 0;
    mrp += g.mrp ?? 0;
  }
  out.sp = sp;
  out.mrp = mrp;
  out.gmv = basis === "sp" ? sp : mrp;
  return out;
}

export interface BrandRow extends Pooled {
  /** Mean live stores carrying the brand. Null until reach is served. */
  stores: number | null;
  /** Mean distinct products listed. Null until reach is served. */
  skus: number | null;
  aspBasis: "measured" | "estimated";
  share: number | null;
}

export interface Reach {
  d: string;
  st: number;
  sk: number;
}

export function brandPool(
  name: string,
  data: {
    national_atoms: Record<string, Atom[]>;
    geo_atoms: Record<string, Record<string, Atom[]>>;
    reach?: Record<string, Reach[]>;
    own: string;
  },
  f: string,
  t: string,
  basis: Basis,
): Omit<BrandRow, "share"> | null {
  const base =
    name === data.own
      ? national(data.geo_atoms, data.own, f, t, basis)
      : (() => {
          const r = atomsIn(data.national_atoms[name], f, t);
          return r.length
            ? deriveGeo(
                name,
                r,
                r.map((x) => x.d),
                basis,
              )
            : null;
        })();
  if (!base) return null;
  const rr = (data.reach?.[name] ?? []).filter((r) => r.d >= f && r.d <= t);
  return {
    ...base,
    stores: rr.length ? rr.reduce((x, r) => x + r.st, 0) / rr.length : null,
    skus: rr.length ? rr.reduce((x, r) => x + r.sk, 0) / rr.length : null,
    aspBasis: base.measured ? "measured" : "estimated",
  };
}

/**
 * The ladder. The market is the own brand (measured) plus every other brand in
 * the category (estimated), on the selected basis — share moves both sides
 * together, so it is always like-for-like within a basis.
 *
 * Excluded brands are NOT removed: the published page filters its exclusion
 * list against `national_atoms` keys, and the excluded brand is not a key — its
 * volume sits inside "Other" — so the filter removes nothing. Reproduced
 * deliberately; dropping it here would restate a client-facing market share.
 */
/**
 * 14zkq6bd1nm — Rival scaling: the own brand's portal actual and the crawl
 * estimate are on different bases (~11.5% unit gap, ~16% ASP gap). Without
 * scaling, the own brand's share is inflated because its numerator is full
 * portal while every rival's is undercounted crawl. The ratio
 * own_portal / own_crawl, computed ONCE over the pool, lifts every rival's
 * crawl GMV to the portal-equivalent basis so share is like-for-like.
 */
function scaledMarketSize(
  bs: Array<Omit<BrandRow, "share">>,
  ownName: string,
  otherName: string,
  basis: Basis,
): {
  scaled: Array<Omit<BrandRow, "share">>;
  size: number;
  ratio: number | null;
} {
  const own = bs.find((b) => b.name === ownName);
  if (!own || !own.measured)
    return {
      scaled: bs,
      size: bs.reduce((a, b) => a + (b.gmv ?? 0), 0),
      ratio: null,
    };

  // Compute the portal/crawl ratio from the own brand's atoms.
  // On MRP: portal value (va) / crawl MRP estimate (mrpe).
  // On SP: portal-derived SP / crawl SP estimate (spe).
  const ownCrawlGmv = basis === "sp" ? own.atoms.spe : own.atoms.mrpe;
  const ownPortalGmv = own.gmv ?? 0;
  if (!ownCrawlGmv || ownCrawlGmv <= 0 || ownPortalGmv <= 0) {
    return {
      scaled: bs,
      size: bs.reduce((a, b) => a + (b.gmv ?? 0), 0),
      ratio: null,
    };
  }
  const ratio = ownPortalGmv / ownCrawlGmv;

  // Scale every non-own, non-other brand's GMV by the ratio.
  const scaled = bs.map((b) => {
    if (b.name === ownName || b.name === otherName || b.measured) return b;
    const scaledGmv = (b.gmv ?? 0) * ratio;
    return { ...b, gmv: scaledGmv };
  });
  const size = scaled.reduce((a, b) => a + (b.gmv ?? 0), 0);
  return { scaled, size, ratio };
}

export function marketPool(
  data: {
    national_atoms: Record<string, Atom[]>;
    geo_atoms: Record<string, Record<string, Atom[]>>;
    reach?: Record<string, Reach[]>;
    own: string;
    other?: string;
  },
  f: string,
  t: string,
  basis: Basis,
): { brands: BrandRow[]; other: BrandRow | null; size: number } {
  const OTHER = data.other ?? "Other";
  const bs = Object.keys(data.national_atoms)
    .map((b) => brandPool(b, data, f, t, basis))
    .filter((x): x is Omit<BrandRow, "share"> => x !== null);

  // Scale rival GMV to portal-equivalent basis before computing shares.
  const { scaled, size } = scaledMarketSize(bs, data.own, OTHER, basis);

  const withShare = (b: Omit<BrandRow, "share">): BrandRow => ({
    ...b,
    share: size ? ((b.gmv ?? 0) / size) * 100 : null,
  });
  const other = scaled.find((b) => b.name === OTHER);
  return {
    brands: scaled
      .filter((b) => b.name !== OTHER)
      .map(withShare)
      .sort((x, y) => (y.gmv ?? 0) - (x.gmv ?? 0)),
    other: other ? withShare(other) : null,
    size,
  };
}

/**
 * The daily lines behind the headline tiles.
 *
 * Market share needs the WHOLE active set per day, not just the own brand, so
 * it is computed here rather than per brand — otherwise the sparkline under the
 * share tile would be the brand's own volume, which is a different shape.
 */
export function ownDailySeries(
  data: {
    national_atoms: Record<string, Atom[]>;
    geo_atoms: Record<string, Record<string, Atom[]>>;
    reach?: Record<string, Reach[]>;
    own: string;
  },
  f: string,
  t: string,
  basis: Basis,
): {
  dates: string[];
  share: Array<number | null>;
  osa: Array<number | null>;
  listing: Array<number | null>;
  ppu: Array<number | null>;
  units: Array<number | null>;
  gmv: Array<number | null>;
  stores: Array<number | null>;
} {
  const days = new Map<string, { cat: number; own: number }>();
  for (const b of Object.keys(data.national_atoms)) {
    for (const r of atomsIn(data.national_atoms[b], f, t)) {
      const e = days.get(r.d) ?? { cat: 0, own: 0 };
      const v = dayValue(r, basis) ?? 0;
      e.cat += v;
      if (b === data.own) e.own += v;
      days.set(r.d, e);
    }
  }
  const nat = national(data.geo_atoms, data.own, f, t, basis);
  const ownByDay = new Map<string, Atom>();
  if (nat) {
    // deriveGeo keeps its input rows in `dates` order; rebuild the per-day map.
    const rows = atomsIn(
      Object.values(data.geo_atoms[data.own] ?? {}).flat(),
      f,
      t,
    );
    for (const r of rows) {
      const a = ownByDay.get(r.d) ?? {
        d: r.d,
        n: 0,
        dn: 0,
        pr: 0,
        au: 0,
        eu: 0,
        spe: 0,
        mrpe: 0,
        va: 0,
      };
      a.n += r.n || 0;
      a.dn += r.dn || 0;
      a.pr += r.pr || 0;
      a.au += r.au || 0;
      a.eu += r.eu || 0;
      a.spe += r.spe || 0;
      a.mrpe += r.mrpe || 0;
      a.va = (a.va ?? 0) + (r.va ?? 0);
      ownByDay.set(r.d, a);
    }
  }
  const st = new Map<string, number>();
  for (const r of (data.reach?.[data.own] ?? []).filter(
    (x) => x.d >= f && x.d <= t,
  )) {
    st.set(r.d, r.st);
  }
  const dates = [...days.keys()].sort();
  return {
    dates,
    share: dates.map((d) => {
      const e = days.get(d);
      return e && e.cat ? (e.own / e.cat) * 100 : null;
    }),
    osa: dates.map((d) => {
      const o = ownByDay.get(d);
      return o && o.dn ? (o.n / o.dn) * 100 : null;
    }),
    listing: dates.map((d) => {
      const o = ownByDay.get(d);
      return o && o.dn ? (o.pr / o.dn) * 100 : null;
    }),
    ppu: dates.map((d) => {
      const o = ownByDay.get(d);
      return o && o.eu ? o.spe / o.eu : null;
    }),
    units: dates.map((d) => ownByDay.get(d)?.au ?? null),
    gmv: dates.map((d) => {
      const o = ownByDay.get(d);
      return o ? dayValue(o, basis) : null;
    }),
    stores: dates.map((d) => st.get(d) ?? null),
  };
}

/** Percentage-POINT delta — for shares, OSA, listing, discount. */
export function pp(cur: number | null, prev: number | null): number | null {
  return cur === null || prev === null ? null : cur - prev;
}

/** Threshold bands the report colours OSA and listing with: <70 red, <85 amber. */
export function bandColor(v: number | null): string | undefined {
  if (v === null || !Number.isFinite(v)) return undefined;
  if (v < 70) return "var(--down)";
  if (v < 85) return "var(--warn)";
  return "var(--up)";
}
